import axiosClient from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance } from "./msalInstance";
import { loginRequest } from "./authConfig";

const headers = {
  "Content-Type": "application/json",
};

const API = axiosClient.create({
  timeout: 35000,
  baseURL: import.meta.env.VITE_API_URL,
  headers,
});

function getMsalAccount() {
  return (
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null
  );
}

function getPersistedBackendToken() {
  try {
    const root = localStorage.getItem("persist:root");
    if (!root) return null;
    const parsed = JSON.parse(root);
    const auth = parsed.auth ? JSON.parse(parsed.auth) : null;
    return auth?.accessToken ?? null;
  } catch {
    return null;
  }
}

function getPersistedProfileToken() {
  try {
    const root = localStorage.getItem("persist:root");
    if (!root) return null;
    const parsed = JSON.parse(root);
    const auth = parsed.auth ? JSON.parse(parsed.auth) : null;
    return auth?.profileToken ?? null;
  } catch {
    return null;
  }
}

function clearPersistedAuthTokens() {
  try {
    const root = localStorage.getItem("persist:root");
    if (!root) return;

    const parsed = JSON.parse(root);
    const auth = parsed.auth ? JSON.parse(parsed.auth) : {};

    delete auth.backendAccessToken;
    delete auth.backendRefreshToken;
    delete auth.profileToken;
    // compat com estados antigos
    delete auth.accessToken;
    delete auth.refreshToken;

    parsed.auth = JSON.stringify(auth);
    localStorage.setItem("persist:root", JSON.stringify(parsed));
  } catch {
    // se der erro de parse, remove tudo para evitar estado corrompido
    localStorage.removeItem("persist:root");
  }
}

async function forceLogout() {
  if (window.__spiritLogoutInProgress) return;
  window.__spiritLogoutInProgress = true;

  clearPersistedAuthTokens();

  const account = getMsalAccount();
  const postLogoutRedirectUri = `${window.location.origin}/`;

  if (account) {
    await msalInstance.logoutRedirect({ account, postLogoutRedirectUri });
    return;
  }

  window.location.assign("/");
}

async function acquireMsalAccessToken(options = {}) {
  const { forceRefresh = false } = options;
  const account = getMsalAccount();
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: loginRequest.scopes,
      account,
      forceRefresh,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const interactive = await msalInstance.acquireTokenPopup({
        scopes: loginRequest.scopes,
        account,
      });
      return interactive.accessToken;
    }
    throw error;
  }
}

async function applyAuthHeaders(config, { forceRefresh } = {}) {
  const backendToken = getPersistedBackendToken();
  const profileToken = getPersistedProfileToken();
  const token = await acquireMsalAccessToken({ forceRefresh });
  const nextHeaders = { ...config.headers };

  if (token || backendToken)
    nextHeaders.Authorization = backendToken ?? `Bearer ${token}`;
  if (profileToken) nextHeaders["x-token"] = profileToken;

  return { ...config, headers: nextHeaders };
}

let requestInterceptorId;
let responseInterceptorId;

function registerInterceptors() {
  if (requestInterceptorId != null) {
    API.interceptors.request.eject(requestInterceptorId);
    API.interceptors.response.eject(responseInterceptorId);
  }

  requestInterceptorId = API.interceptors.request.use(
  async (config) => {
    try {
      return await applyAuthHeaders(config, { forceRefresh: false });
    } catch (error) {
      console.error("Erro ao obter token:", error);
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error)
  );

  responseInterceptorId = API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (
      config &&
      response?.status === 401 &&
      !config._msal401Retried &&
      getMsalAccount()
    ) {
      config._msal401Retried = true;
      try {
        const retriedConfig = await applyAuthHeaders(config, {
          forceRefresh: true,
        });
        return API.request(retriedConfig);
      } catch (retryError) {
        console.error("Falha ao renovar token após 401:", retryError);
      }
    }

    if (response && (response.status === 401 || response.status === 403)) {
      if (config && !config._didForceLogout) {
        config._didForceLogout = true;
        try {
          await forceLogout();
        } catch (logoutError) {
          console.error("Falha ao efetuar logout após 401/403:", logoutError);
          window.location.assign("/");
        }
      }
    }

    return Promise.reject(error);
  }
  );
}

registerInterceptors();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (requestInterceptorId != null) {
      API.interceptors.request.eject(requestInterceptorId);
      API.interceptors.response.eject(responseInterceptorId);
      requestInterceptorId = null;
      responseInterceptorId = null;
    }
  });
}

export default API;
